import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ListChecks, CheckCircle2, Circle, Clock, Plus, Trash2, Send, Pencil,
  Check, X, RotateCcw, SkipForward, MessageSquare, ArrowLeft, Sparkles, Users,
} from "lucide-react";
import {
  createSessionAgreement, deleteSessionAgreement, listCoupleSessions, listPatientSessions,
  listSessionAgreements, publishSessionAgreements, updateSessionAgreement,
} from "../../application/sessions";
import type { Session, SessionAgreement } from "../../../session";
import Button from "../../../../shared/ui/button/Button";

const STATUS_STYLES: Record<string, string> = {
  pending:     "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  in_progress: "bg-accent-tint text-accent-text",
  completed:   "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
  skipped:     "bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400",
};

const STATUS_ICONS: Record<string, JSX.Element> = {
  pending:     <Clock className="w-3 h-3" />,
  in_progress: <RotateCcw className="w-3 h-3" />,
  completed:   <CheckCircle2 className="w-3 h-3" />,
  skipped:     <SkipForward className="w-3 h-3" />,
};

interface Props {
  patientId: string;
  isCouple?: boolean;
}

interface SessionCounts { total: number; completed: number }

export default function PatientAgreementsTab({ patientId, isCouple }: Props) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionCounts, setSessionCounts] = useState<Record<string, SessionCounts>>({});
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [agreements, setAgreements] = useState<SessionAgreement[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const [mobilePanel, setMobilePanel] = useState<"sessions" | "agreements">("sessions");

  // Load all completed sessions + pre-fetch published counts for badges
  useEffect(() => {
    const fetch = isCouple
      ? listCoupleSessions(patientId, { limit: 50 })
      : listPatientSessions(patientId, { limit: 50 });
    fetch
      .then((data) => {
        const list: Session[] = Array.isArray(data) ? data : (data as { sessions?: Session[] }).sessions ?? [];
        const completed = list
          .filter((s) => s.status === "completed")
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setSessions(completed);
        if (completed.length > 0) setSelectedSessionId(completed[0].id);
        // Fetch published counts for all sessions (for badges)
        Promise.all(
          completed.map((s) =>
            listSessionAgreements(s.id).then((ag) => ({
              id: s.id,
              total: ag.filter((a) => a.state === "published").length,
              completed: ag.filter((a) => a.state === "published" && a.status === "completed").length,
            }))
          )
        ).then((results) => {
          const map: Record<string, SessionCounts> = {};
          results.forEach(({ id, total, completed }) => { map[id] = { total, completed }; });
          setSessionCounts(map);
        });
      })
      .finally(() => setLoadingSessions(false));
  }, [patientId, isCouple]);

  // Load agreements for selected session
  useEffect(() => {
    if (!selectedSessionId) return;
    setLoadingAgreements(true);
    setAgreements([]);
    setNewText("");
    setEditingId(null);
    listSessionAgreements(selectedSessionId)
      .then((data) => setAgreements(data))
      .finally(() => setLoadingAgreements(false));
  }, [selectedSessionId]);

  // Keep left-panel badge in sync with current session agreements
  useEffect(() => {
    if (!selectedSessionId) return;
    const pub = agreements.filter((a) => a.state === "published");
    setSessionCounts((prev) => ({
      ...prev,
      [selectedSessionId]: {
        total: pub.length,
        completed: pub.filter((a) => a.status === "completed").length,
      },
    }));
  }, [agreements, selectedSessionId]);

  const drafts = agreements.filter((a) => a.state === "draft");
  const published = agreements.filter((a) => a.state === "published");

  const handleAdd = async () => {
    const text = newText.trim();
    if (!text || !selectedSessionId) return;
    setAdding(true);
    try {
      const data = await createSessionAgreement(selectedSessionId, text);
      setAgreements((prev) => [...prev, data]);
      setNewText("");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteSessionAgreement(id);
    setAgreements((prev) => prev.filter((a) => a.id !== id));
  };

  const startEdit = (a: SessionAgreement) => {
    setEditingId(a.id);
    setEditingText(a.text);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const saveEdit = async (id: string) => {
    const text = editingText.trim();
    if (!text) { setEditingId(null); return; }
    const data = await updateSessionAgreement(id, { text });
    setAgreements((prev) => prev.map((a) => (a.id === id ? data : a)));
    setEditingId(null);
  };

  const handlePublish = async () => {
    if (!selectedSessionId || drafts.length === 0) return;
    if (!window.confirm(t("session.agreements.publishConfirm"))) return;
    setPublishing(true);
    try {
      const data = await publishSessionAgreements(selectedSessionId);
      setAgreements(data);
    } finally {
      setPublishing(false);
    }
  };

  const selectedSession = sessions.find((s) => s.id === selectedSessionId) ?? null;

  if (loadingSessions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="glass-effect glass-border border rounded-2xl p-12 text-center">
        <ListChecks className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-content-tertiary">{t("patient.patientAgreements.empty")}</p>
      </div>
    );
  }

  return (
    <div className="flex xl:h-[calc(100vh-16rem)] overflow-hidden glass-effect glass-border border rounded-2xl">
      {/* Left panel — session list */}
      <div className={`${mobilePanel === "agreements" ? "hidden" : "flex"} xl:flex w-full xl:w-64 shrink-0 flex-col border-r border-gray-200 dark:border-white/10`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-brand-500" />
            {t("patient.patientAgreements.title")}
          </h2>
        </div>

        <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
          {sessions.map((session) => {
            const isSelected = selectedSessionId === session.id;
            const counts = sessionCounts[session.id];
            const allDone = counts && counts.total > 0 && counts.completed === counts.total;
            return (
              <li key={session.id}>
                <button
                  onClick={() => { setSelectedSessionId(session.id); setMobilePanel("agreements"); }}
                  className={`w-full text-left px-4 py-3 transition-colors border-l-2 ${
                    isSelected
                      ? "bg-brand-50 dark:bg-brand-900/20 border-brand-500"
                      : "hover:bg-gray-50 dark:hover:bg-white/5 border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className={`text-sm font-medium truncate ${isSelected ? "text-brand-700 dark:text-brand-300" : "text-gray-800 dark:text-gray-200"}`}>
                      {session.title}
                    </p>
                    {session.type === "couple" && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
                        <Users className="w-2.5 h-2.5" />
                        {t("session.type.couple", "Pareja")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-content-tertiary">
                      {new Date(session.date).toLocaleDateString()}
                    </span>
                    {counts && counts.total > 0 && (
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                        allDone
                          ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400"
                      }`}>
                        {counts.completed}/{counts.total}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right panel */}
      <div className={`${mobilePanel === "sessions" ? "hidden" : "flex"} xl:flex flex-1 flex-col overflow-hidden`}>
        {selectedSession ? (
          <>
            {/* Session header + publish button */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-white/10 shrink-0 flex flex-col xl:flex-row xl:items-center gap-3 xl:justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobilePanel("sessions")}
                  className="xl:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {selectedSession.title}
                  </h3>
                  <p className="text-xs text-content-tertiary mt-0.5">
                    {new Date(selectedSession.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handlePublish}
                disabled={drafts.length === 0 || publishing}
                loading={publishing}
                startIcon={<Send className="w-3.5 h-3.5" />}
                className="self-start xl:self-auto"
              >
                {t("session.agreements.publish")}
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {loadingAgreements ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <>
                  {/* Draft agreements */}
                  {drafts.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-content-tertiary uppercase tracking-wider px-1">
                        {t("session.agreements.drafts")} ({drafts.length})
                      </p>
                      <ul className="space-y-2">
                        {drafts.map((a) => (
                          <li
                            key={a.id}
                            className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] group"
                          >
                            <div className="flex-1 min-w-0">
                              {editingId === a.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    ref={editInputRef}
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveEdit(a.id);
                                      if (e.key === "Escape") setEditingId(null);
                                    }}
                                    className="flex-1 text-sm bg-transparent border-b border-brand-400 outline-none text-content-primary py-0.5"
                                  />
                                  <button onClick={() => saveEdit(a.id)} className="text-green-500 hover:text-green-600">
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => setEditingId(null)} className="text-content-tertiary hover:text-gray-600">
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-start gap-2">
                                  <p className="text-sm text-gray-800 dark:text-gray-200">{a.text}</p>
                                  {a.source === "ai" && (
                                    <span title="AI" className="shrink-0 mt-0.5">
                                      <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {editingId !== a.id && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <button
                                  onClick={() => startEdit(a)}
                                  className="p-1 rounded-lg text-content-tertiary hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-white/5"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(a.id)}
                                  className="p-1 rounded-lg text-content-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Add new */}
                  <div className="flex gap-2">
                    <input
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                      placeholder={t("session.agreements.addPlaceholder")}
                      className="field-base field-default flex-1 px-4 py-2.5"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAdd}
                      disabled={!newText.trim() || adding}
                      loading={adding}
                      startIcon={<Plus className="w-3.5 h-3.5" />}
                    >
                      {t("session.agreements.add")}
                    </Button>
                  </div>

                  {/* Published agreements */}
                  {published.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-content-tertiary uppercase tracking-wider px-1">
                        {t("session.agreements.published")} ({published.length})
                      </p>
                      <ul className="space-y-2">
                        {published.map((a) => (
                          <PublishedAgreementRow key={a.id} agreement={a} />
                        ))}
                      </ul>
                    </div>
                  )}

                  {agreements.length === 0 && !loadingAgreements && (
                    <p className="text-center text-sm text-content-tertiary py-8 italic">
                      {t("session.agreements.empty")}
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-content-tertiary">{t("patient.patientAgreements.selectSession")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PublishedAgreementRow({ agreement }: { agreement: SessionAgreement }) {
  const { t } = useTranslation();
  const status = agreement.status ?? "pending";
  const isCompleted = status === "completed";

  return (
    <li className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.01]">
      <div className="flex items-start gap-3">
        {isCompleted ? (
          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
        ) : (
          <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-0.5 shrink-0" />
        )}
        <p className={`flex-1 text-sm leading-relaxed ${isCompleted ? "line-through text-content-tertiary" : "text-content-secondary"}`}>
          {agreement.text}
        </p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_STYLES[status] ?? STATUS_STYLES.pending}`}>
          {STATUS_ICONS[status]}
          {t(`portal.agreement.status.${status}`, status)}
        </span>
      </div>

      {(agreement.patient_comments?.length ?? (agreement.patient_comment ? 1 : 0)) > 0 && (
        <div className="ml-7 space-y-1.5">
          {(agreement.patient_comments?.length ? agreement.patient_comments : agreement.patient_comment ? [agreement.patient_comment] : []).map((comment, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-accent-tint border border-accent/20 px-3 py-2">
              <MessageSquare className="w-3.5 h-3.5 text-accent-text mt-0.5 shrink-0" />
              <p className="text-xs text-accent-text leading-relaxed">{comment}</p>
            </div>
          ))}
        </div>
      )}

      {agreement.published_at && (
        <p className="ml-7 text-xs text-content-tertiary flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {t("session.agreements.publishedOn")} {new Date(agreement.published_at).toLocaleDateString()}
        </p>
      )}
    </li>
  );
}
